// Session 58 · Admin → Inventory → Venue → Team page.
// Full P0: search existing members, assign role, workspace scoping, revoke,
// change role, resend access email. Uses the shared toast/table styles from
// other admin pages so it slots into the existing admin visual language.
import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { toast, Toaster } from "sonner";

const ROLE_META = {
  owner:   { label: "Owner",   bg: "#EDE2CD", fg: "#5A410D",
             blurb: "Full venue and workspace management" },
  manager: { label: "Manager", bg: "#DFEEE1", fg: "#1B4634",
             blurb: "Manage pricing, availability, photos, bookings, and reports" },
  staff:   { label: "Staff",   bg: "#E5EAF5", fg: "#243671",
             blurb: "View venue and workspaces; limited edits when granted" },
};

export default function AdminVenueTeam() {
  const { venueId } = useParams();
  const [team, setTeam] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [busy, setBusy] = useState(false);

  // Assign panel state
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [role, setRole] = useState("manager");
  const [selectedWs, setSelectedWs] = useState([]);
  const [sendEmail, setSendEmail] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/venues/${venueId}/team`);
      setTeam(r.data);
      // fetch workspaces to power the scope picker
      const w = await api.get(`/admin/workspaces?venue_id=${venueId}`);
      setWorkspaces(w.data?.workspaces || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load team");
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  // Debounced typeahead
  useEffect(() => {
    if (search.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/admin/members/search`, { params: { q: search, limit: 12 } });
        setResults(r.data?.members || []);
      } catch { /* ignore */ }
    }, 220);
    return () => clearTimeout(t);
  }, [search]);

  const assign = async () => {
    if (!selected) { toast.error("Pick a member first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/venues/${venueId}/team/assign`, {
        user_id: selected.user_id,
        role,
        workspace_ids: selectedWs,
        send_email: sendEmail,
      });
      const e = r.data?.email;
      if (e?.sent) toast.success(`Assigned. Email delivered (${e.email_id.slice(0,8)}…)`);
      else if (e?.error) toast.warning(`Assigned. Email failed. ${e.error}`);
      else toast.success("Assigned.");
      // reset picker
      setSelected(null); setSearch(""); setSelectedWs([]); setResults([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assignment failed");
    } finally { setBusy(false); }
  };

  const changeRole = async (grant, newRole) => {
    if (!window.confirm(`Change ${grant.user.email}'s role to ${newRole}?`)) return;
    try {
      await api.post(`/admin/venues/${venueId}/team/${grant.grant_id}/role`,
        { role: newRole, workspace_ids: grant.workspace_ids || [] });
      toast.success(`Role changed to ${newRole}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Role change failed");
    }
  };

  const revoke = async (grant) => {
    if (!window.confirm(`Revoke ${grant.user.email}'s access to this venue?`)) return;
    try {
      await api.post(`/admin/venues/${venueId}/team/${grant.grant_id}/revoke`);
      toast.success("Access revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const resendEmail = async (grant) => {
    try {
      const r = await api.post(`/admin/venues/${venueId}/team/${grant.grant_id}/resend-email`);
      if (r.data?.ok) toast.success(`Access email re-sent (${r.data.email_id.slice(0,8)}…)`);
      else toast.warning(`Send failed. ${r.data?.error || "unknown"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    }
  };

  const toggleWs = (wid) => {
    setSelectedWs((s) => s.includes(wid) ? s.filter((x) => x !== wid) : [...s, wid]);
  };

  if (!team) return <div style={{ padding: 40 }}><Toaster richColors position="top-center" /> Loading…</div>;

  return (
    <div style={{ background: "#FAF8F3", minHeight: "100vh", padding: "32px 40px",
                   fontFamily: "Georgia, serif", color: "#1B2340" }}
         data-testid="admin-venue-team">
      <Toaster richColors position="top-center" />
      <div style={{ maxWidth: 1160, margin: "0 auto" }}>
        <Link to="/admin/inventory" style={{ fontSize: 12, color: "rgba(27,35,64,0.65)",
                                              textDecoration: "none" }}>← Inventory</Link>
        <h1 style={{ fontSize: 34, fontWeight: 500, margin: "10px 0 4px",
                     fontFamily: "Cormorant Garamond, Georgia, serif" }}>
          Team · {team.venue.name}
        </h1>
        <p style={{ fontSize: 13, color: "rgba(27,35,64,0.7)", margin: "0 0 24px" }}>
          Assign existing Métier members as owners, managers, or staff on this venue.
          Never creates duplicate accounts or Stripe accounts.
        </p>

        {/* -------- Assign panel -------- */}
        <section style={panel}>
          <h2 style={h2}>Assign existing member</h2>
          <div style={{ position: "relative", marginTop: 8 }}>
            <input type="text" value={search}
                   onChange={(e) => { setSearch(e.target.value); setSelected(null); }}
                   placeholder="Search by name or email…"
                   data-testid="team-member-search" style={inputBase} />
            {results.length > 0 && !selected && (
              <div style={dropdown} data-testid="team-member-results">
                {results.map((m) => (
                  <button key={m.user_id} type="button"
                    onClick={() => { setSelected(m); setSearch(m.email); setResults([]); }}
                    data-testid={`team-member-option-${m.user_id}`}
                    style={dropdownRow}>
                    <div style={{ fontWeight: 600 }}>{m.name || m.email}</div>
                    <div style={{ fontSize: 11, color: "rgba(27,35,64,0.6)" }}>{m.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selected && (
            <div style={{ marginTop: 10, padding: "10px 14px", background: "#F1EBDE",
                          borderRadius: 8, fontSize: 13 }}>
              Selected: <b>{selected.name || selected.email}</b> <span style={{color:"rgba(27,35,64,0.6)"}}>({selected.email})</span>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 18 }}>
            <div>
              <label style={lbl}>Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)}
                       data-testid="team-role-select" style={inputBase}>
                <option value="owner">Owner · full control</option>
                <option value="manager">Manager · day-to-day operations</option>
                <option value="staff">Staff · view-only + granted edits</option>
              </select>
              <div style={{ fontSize: 12, color: "rgba(27,35,64,0.7)", marginTop: 6 }}>
                {ROLE_META[role].blurb}
              </div>
            </div>
            <div>
              <label style={lbl}>Workspace scope</label>
              <div style={{ ...inputBase, minHeight: 80, padding: 8, overflow: "auto" }}>
                {workspaces.length === 0 && <div style={{fontSize:12,color:"rgba(27,35,64,0.5)"}}>No workspaces under this venue</div>}
                {workspaces.map((w) => (
                  <label key={w.workspace_id} style={{ display: "flex", alignItems: "center",
                                                       gap: 6, padding: "4px 2px",
                                                       fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox" checked={selectedWs.includes(w.workspace_id)}
                           onChange={() => toggleWs(w.workspace_id)}
                           data-testid={`team-scope-ws-${w.workspace_id}`} />
                    {w.name || w.workspace_id}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "rgba(27,35,64,0.6)", marginTop: 4 }}>
                Leave empty for venue-wide access. Populate to restrict to those workspaces only.
              </div>
            </div>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 16, fontSize: 13 }}>
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                   data-testid="team-send-email-checkbox" />
            Send access-granted email
          </label>

          <button type="button" onClick={assign} disabled={busy || !selected}
                   data-testid="team-assign-submit"
                   style={{ ...btnPrimary, marginTop: 18,
                            opacity: (busy || !selected) ? 0.5 : 1 }}>
            {busy ? "Working…" : "Assign to venue"}
          </button>
        </section>

        {/* -------- Team roster -------- */}
        <section style={{ ...panel, marginTop: 26 }}>
          <h2 style={h2}>Current team ({team.count})</h2>
          {team.team.length === 0 && (
            <p style={{ fontSize: 13, color: "rgba(27,35,64,0.65)", marginTop: 10 }}>
              No teammates assigned yet. Use the panel above to add the first one.
            </p>
          )}
          {team.team.length > 0 && (
            <div style={{ overflow: "auto", marginTop: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
                      data-testid="team-roster-table">
                <thead>
                  <tr style={{ textAlign: "left", background: "rgba(184,145,58,0.08)" }}>
                    <th style={th}>Member</th>
                    <th style={th}>Role</th>
                    <th style={th}>Scope</th>
                    <th style={th}>Last active</th>
                    <th style={th}>Email</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {team.team.map((g) => {
                    const meta = ROLE_META[g.role] || ROLE_META.staff;
                    return (
                      <tr key={g.grant_id} style={{ borderTop: "1px solid rgba(27,35,64,0.08)" }}
                          data-testid={`team-row-${g.grant_id}`}>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{g.user.name || "—"}</div>
                          <div style={{ fontSize: 11, color: "rgba(27,35,64,0.65)" }}>{g.user.email}</div>
                        </td>
                        <td style={td}>
                          <select value={g.role} onChange={(e) => changeRole(g, e.target.value)}
                                   data-testid={`team-row-role-${g.grant_id}`}
                                   style={{ padding: "4px 8px", background: meta.bg, color: meta.fg,
                                            border: "none", borderRadius: 999, fontWeight: 700,
                                            fontSize: 11, letterSpacing: "0.04em", cursor: "pointer" }}>
                            <option value="owner">OWNER</option>
                            <option value="manager">MANAGER</option>
                            <option value="staff">STAFF</option>
                          </select>
                        </td>
                        <td style={td}>
                          {g.workspaces && g.workspaces.length > 0
                            ? <div>
                                <div style={{fontSize:11,color:"rgba(27,35,64,0.6)",textTransform:"uppercase",letterSpacing:"0.06em"}}>Workspaces</div>
                                {g.workspaces.map(w => <div key={w.workspace_id}>{w.name}</div>)}
                              </div>
                            : <span style={{fontSize:11,color:"#1B4634",fontWeight:700,letterSpacing:"0.04em"}}>VENUE-WIDE</span>}
                        </td>
                        <td style={td}>{fmtDate(g.user.last_active_at)}</td>
                        <td style={td}>
                          {g.last_email_sent_at
                            ? <span style={{fontSize:11,color:"#1B4634"}}>✓ sent {fmtDate(g.last_email_sent_at)}</span>
                            : <span style={{fontSize:11,color:"rgba(27,35,64,0.55)"}}>—</span>}
                          <div>
                            <button type="button" onClick={() => resendEmail(g)}
                                     data-testid={`team-row-resend-${g.grant_id}`}
                                     style={linkBtn}>Resend email</button>
                          </div>
                        </td>
                        <td style={td}>
                          <button type="button" onClick={() => revoke(g)}
                                   data-testid={`team-row-revoke-${g.grant_id}`}
                                   style={{...linkBtn, color:"#B84A3A"}}>Revoke</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// helpers
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";

// styles
const panel = { background: "#fff", borderRadius: 14, padding: "22px 26px",
                 border: "1px solid rgba(27,35,64,0.08)" };
const h2 = { fontFamily: "Cormorant Garamond, Georgia, serif", fontSize: 22,
             margin: 0, fontWeight: 500 };
const lbl = { display: "block", fontSize: 11, letterSpacing: "0.08em",
              textTransform: "uppercase", color: "rgba(27,35,64,0.65)", marginBottom: 6 };
const inputBase = { width: "100%", boxSizing: "border-box", padding: "9px 12px",
                     background: "#FAF8F3", border: "1px solid rgba(27,35,64,0.15)",
                     borderRadius: 8, fontSize: 14, fontFamily: "inherit", color: "#1B2340" };
const dropdown = { position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5,
                    background: "#fff", border: "1px solid rgba(27,35,64,0.15)",
                    borderRadius: 8, marginTop: 4, maxHeight: 240, overflow: "auto",
                    boxShadow: "0 8px 24px rgba(27,35,64,0.12)" };
const dropdownRow = { display: "block", width: "100%", padding: "10px 14px",
                       background: "#fff", border: "none", borderBottom: "1px solid rgba(27,35,64,0.06)",
                       textAlign: "left", cursor: "pointer" };
const btnPrimary = { padding: "10px 22px", background: "#B8913A", color: "#FAF8F3",
                      border: "none", borderRadius: 999, cursor: "pointer",
                      fontSize: 13, fontWeight: 600, letterSpacing: "0.04em" };
const th = { padding: "10px 12px", fontSize: 11, letterSpacing: "0.08em",
             textTransform: "uppercase", color: "rgba(27,35,64,0.65)", fontWeight: 700 };
const td = { padding: "10px 12px", verticalAlign: "top" };
const linkBtn = { background: "none", border: "none", padding: 0,
                   color: "#1B2340", fontSize: 12, cursor: "pointer",
                   textDecoration: "underline" };
